<template>
  <div class="all-listings-page">
    <header class="page-header">
      <div class="page-header-inner">
        <div>
          <div class="page-title">Todos os Anúncios</div>
          <div class="page-subtitle">Visão geral dos imóveis, alojamentos e viaturas</div>
        </div>
        <q-btn flat round icon="refresh" color="white" aria-label="Actualizar anúncios" :loading="loading" @click="load" />
      </div>
    </header>

    <main class="content-wrapper">
      <nav class="type-tabs" aria-label="Filtrar por tipo de anúncio">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          type="button"
          class="type-tab"
          :class="{ 'type-tab--active': selectedType === tab.value }"
          @click="selectType(tab.value)"
        >
          <q-icon :name="tab.icon" size="19px" />
          <span>{{ tab.label }}</span>
          <span v-if="selectedType === tab.value && pagination.total != null" class="type-tab-count">{{ pagination.total }}</span>
        </button>
      </nav>

      <div class="results-heading">
        <div>
          <strong>{{ pagination.total ?? listings.length }}</strong>
          <span>{{ selectedTab.countLabel }}</span>
        </div>
      </div>

      <div v-if="loading" class="listing-grid">
        <q-card v-for="n in 8" :key="n" flat bordered class="listing-card">
          <q-skeleton height="190px" />
          <q-card-section><q-skeleton type="text" width="45%" /><q-skeleton type="text" width="85%" /><q-skeleton type="text" width="65%" /></q-card-section>
        </q-card>
      </div>

      <div v-else-if="error" class="empty-state">
        <q-icon name="cloud_off" size="48px" color="grey-5" />
        <h2>Não foi possível carregar os anúncios</h2>
        <p>{{ error }}</p>
        <q-btn color="primary" icon="refresh" label="Tentar novamente" unelevated @click="load" />
      </div>

      <div v-else-if="!listings.length" class="empty-state">
        <q-icon :name="selectedTab.icon" size="48px" color="grey-5" />
        <h2>Nenhum anúncio encontrado</h2>
        <p>Não há anúncios nesta categoria.</p>
      </div>

      <div v-else class="listing-grid">
        <q-card
          v-for="item in listings"
          :key="`${selectedType}-${item.id || item.slug}`"
          flat
          bordered
          class="listing-card"
          tabindex="0"
          role="link"
          :aria-label="`Ver anúncio: ${item.title}`"
          @click="openDetails(item)"
          @keydown.enter.self.prevent="openDetails(item)"
        >
          <div class="card-image-wrap">
            <q-img :src="imageFor(item)" :ratio="16 / 10" class="card-image">
              <template #loading><q-spinner-gears color="white" /></template>
              <template #error><div class="image-fallback"><q-icon :name="selectedTab.icon" size="42px" /></div></template>
            </q-img>
            <span class="category-badge">{{ categoryLabel(item) }}</span>
            <span v-if="highlightLabel(item)" class="highlight-badge" :class="{ 'highlight-badge--plan': item.promotion?.badge_label }">
              <q-icon :name="highlightIcon(item)" size="13px" />{{ highlightLabel(item) }}
            </span>
            <span class="status-badge" :class="statusClass(item.status)">
              <i />{{ statusLabel(item.status) }}
            </span>
          </div>

          <q-card-section class="card-body">
            <div class="card-price">{{ priceLabel(item) }}</div>
            <h2 class="card-title">{{ item.title }}</h2>
            <div class="card-location"><q-icon name="location_on" size="15px" /><span>{{ item.location || "Localização não indicada" }}</span></div>
            <div class="card-facts">
              <template v-if="selectedType === 'property'">
                <span><q-icon name="bed" /> {{ item.bedroom_amount || 0 }} quartos</span>
                <span><q-icon name="bathtub" /> {{ item.bathroom_amount || 0 }} banhos</span>
                <span><q-icon name="square_foot" /> {{ item.area || 0 }} m²</span>
              </template>
              <template v-else-if="selectedType === 'accommodation'">
                <span><q-icon name="groups" /> {{ maxCapacity(item) }} hóspedes</span>
                <span><q-icon name="bed" /> {{ maxBedrooms(item) }} quartos</span>
                <span><q-icon name="meeting_room" /> {{ activeUnits(item).length }} unidades</span>
              </template>
              <template v-else>
                <span><q-icon name="event_seat" /> {{ item.seats || "—" }} lugares</span>
                <span><q-icon name="settings" /> {{ transmissionLabel(item.transmission) }}</span>
                <span><q-icon name="local_gas_station" /> {{ item.fuel || "—" }}</span>
              </template>
            </div>
            <div class="card-footer">
              <div class="agent">
                <img :src="item.user?.photo || fallbackAgent" alt="" @error="setFallback" />
                <span>{{ item.user?.name || "Agente Golden Key" }}</span>
              </div>
              <q-icon name="arrow_forward" color="grey-6" size="18px" />
            </div>
          </q-card-section>
        </q-card>
      </div>

      <div v-if="!loading && !error && pagination.last_page > 1" class="pagination-row">
        <q-pagination
          v-model="page"
          :max="pagination.last_page"
          :max-pages="6"
          direction-links
          boundary-links
          color="primary"
          active-design="unelevated"
          @update:model-value="load"
        />
      </div>
    </main>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

const router = useRouter();
const store = useStore();
const fallbackAgent = "https://amzn-s3-goldenkey.s3.us-east-2.amazonaws.com/properties/icon2.png";
const perPage = 48;
const selectedType = ref("property");
const page = ref(1);
const listings = ref([]);
const pagination = ref({});
const loading = ref(false);
const error = ref("");
const tabs = [
  { value: "property", label: "Imóveis", countLabel: " imóveis", icon: "home_work" },
  { value: "accommodation", label: "Alojamentos", countLabel: " alojamentos", icon: "hotel" },
  { value: "rental_vehicle", label: "Rent-a-car", countLabel: " viaturas", icon: "directions_car" },
];
const selectedTab = computed(() => tabs.find((tab) => tab.value === selectedType.value) || tabs[0]);

function activeUnits(item) {
  const units = item.units || [];
  return units.filter((unit) => unit.active !== false);
}
function maxCapacity(item) {
  return Math.max(0, ...activeUnits(item).map((unit) => Number(unit.capacity || 0)));
}
function maxBedrooms(item) {
  return Math.max(0, ...activeUnits(item).map((unit) => Number(unit.bedroom_amount || 0)));
}
function categoryLabel(item) {
  if (selectedType.value === "property") return item.objective || item.property_type || "Imóvel";
  if (selectedType.value === "accommodation") return item.type?.name || "Alojamento";
  return item.category?.name || "Rent-a-car";
}
function imageFor(item) {
  return item.primary_image_url || item.primary_image?.url || item.image?.[0]?.url || item.images?.[0]?.url || "";
}
function formatMoney(value, currency = "MZN") {
  if (value == null || value === "") return "Preço sob consulta";
  return `${Number(value).toLocaleString("pt-MZ")} ${currency || "MZN"}`;
}
function priceLabel(item) {
  if (selectedType.value === "property") return formatMoney(item.price, item.currency);
  if (selectedType.value === "accommodation") {
    const unit = [...activeUnits(item)].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))[0];
    return unit ? `${formatMoney(unit.price, unit.currency)} / noite` : "Preço sob consulta";
  }
  return item.terms?.daily_price != null
    ? `${formatMoney(item.terms.daily_price, item.terms.currency)} / dia`
    : "Preço sob consulta";
}
function highlightLabel(item) {
  return item.promotion?.badge_label ||
    (item.promotion?.editorial_featured || item.is_featured ? "Destaque editorial" : null) ||
    (item.verified || item.verified_at ? "Verificado" : null);
}
function highlightIcon(item) {
  return item.promotion?.editorial_featured || item.is_featured
    ? "star"
    : item.promotion?.badge_label
      ? "workspace_premium"
      : "verified";
}
function statusLabel(status) {
  return {
    active: "Activo",
    published: "Publicado",
    pending_review: "Aguarda validação",
    draft: "Rascunho",
    rejected: "Requer correcção",
    inactive: "Inactivo",
    expired: "Expirado",
  }[status] || "Estado indisponível";
}
function statusClass(status) {
  if (status === "active" || status === "published") return "status-badge--active";
  if (status === "pending_review") return "status-badge--pending";
  if (status === "draft") return "status-badge--draft";
  if (["rejected", "expired"].includes(status)) return "status-badge--attention";
  return "status-badge--inactive";
}
function transmissionLabel(value) {
  return value === "automatic" ? "Automática" : value === "manual" ? "Manual" : value || "—";
}
function setFallback(event) {
  event.target.src = fallbackAgent;
}
function openDetails(item) {
  const path = selectedType.value === "property"
    ? `/imovel/${item.slug}`
    : selectedType.value === "accommodation"
      ? `/alojamentos/${item.slug}`
      : `/rent-a-car/${item.slug}`;
  router.push(path);
}
async function load() {
  loading.value = true;
  error.value = "";
  try {
    const args = { page: page.value, perPage, publishedOnly: true };
    let result;
    if (selectedType.value === "property") {
      result = await store.dispatch("imovel/getAllImovel", args);
    } else {
      const moduleName = selectedType.value === "accommodation" ? "accommodations" : "rentalVehicles";
      result = await store.dispatch(`${moduleName}/list`, {
        page: page.value,
        per_page: perPage,
        all: true,
        publishedOnly: true,
      });
    }
    listings.value = result?.data || [];
    pagination.value = result || {};
  } catch (requestError) {
    listings.value = [];
    pagination.value = {};
    error.value = requestError.response?.data?.message || "Verifique a ligação ao servidor e tente novamente.";
  } finally {
    loading.value = false;
  }
}
function selectType(type) {
  if (selectedType.value === type) return;
  selectedType.value = type;
  page.value = 1;
  load();
}
onMounted(load);
</script>

<style scoped>
.all-listings-page { min-height: 100vh; background: #f4f6fb; }
.page-header { padding: 20px 24px; background: #0d1b2a; }
.page-header-inner { display: flex; max-width: 1400px; margin: 0 auto; align-items: center; justify-content: space-between; gap: 14px; }
.page-title { color: #fff; font-size: 20px; font-weight: 800; }
.page-subtitle { margin-top: 3px; color: rgba(212,175,55,.78); font-size: 12px; }
.content-wrapper { max-width: 1400px; margin: 28px auto; padding: 0 20px 52px; }
.type-tabs { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 0 16px; border-bottom: 1px solid #e1e7f0; }
.type-tab { display: inline-flex; align-items: center; gap: 8px; min-height: 40px; padding: 0 14px; border: 1px solid #e1e7f0; border-radius: 10px; color: #475569; background: #fff; font-size: 12px; font-weight: 700; cursor: pointer; }
.type-tab--active { border-color: #0d1b2a; color: #fff; background: #0d1b2a; }
.type-tab-count { min-width: 22px; padding: 2px 6px; border-radius: 10px; color: #0d1b2a; background: #d4af37; font-size: 10px; text-align: center; }
.results-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 2px; color: #64748b; font-size: 12px; }
.results-heading strong { margin-right: 5px; color: #0d1b2a; font-size: 15px; }
.listing-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 20px; }
.listing-card { overflow: hidden; border-color: #e8ecf4; border-radius: 14px; background: #fff; transition: transform .2s ease, box-shadow .2s ease; cursor: pointer; }
.listing-card:hover, .listing-card:focus-visible { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(13,27,42,.1); outline: none; }
.card-image-wrap { position: relative; overflow: hidden; background: #e8ecf4; }
.card-image { display: block; }
.image-fallback { display: grid; width: 100%; height: 100%; min-height: 140px; place-items: center; color: #94a3b8; }
.category-badge, .highlight-badge, .status-badge { position: absolute; z-index: 2; display: inline-flex; align-items: center; gap: 5px; padding: 5px 9px; border-radius: 14px; font-size: 9px; font-weight: 800; box-shadow: 0 2px 7px rgba(13,27,42,.12); }
.category-badge { bottom: 10px; left: 10px; max-width: 55%; overflow: hidden; color: #fff; background: rgba(13,27,42,.86); text-overflow: ellipsis; white-space: nowrap; }
.highlight-badge { top: 10px; left: 10px; max-width: 60%; overflow: hidden; color: #0d1b2a; background: rgba(255,255,255,.95); text-overflow: ellipsis; white-space: nowrap; }
.highlight-badge--plan { color: #0d1b2a; background: rgba(212,175,55,.96); }
.status-badge { top: 10px; right: 10px; max-width: 52%; overflow: hidden; color: #fff; text-overflow: ellipsis; white-space: nowrap; }
.status-badge i { width: 6px; height: 6px; flex: 0 0 6px; border-radius: 50%; background: currentColor; }
.status-badge--active { color: #166534; background: rgba(220,252,231,.97); }
.status-badge--pending { color: #1d4ed8; background: rgba(219,234,254,.97); }
.status-badge--draft { color: #854d0e; background: rgba(254,243,199,.97); }
.status-badge--inactive { color: #475569; background: rgba(226,232,240,.97); }
.status-badge--attention { color: #991b1b; background: rgba(254,226,226,.97); }
.card-body { padding: 14px !important; }
.card-price { color: #0d1b2a; font-size: 16px; font-weight: 800; }
.card-title { display: -webkit-box; min-height: 38px; margin: 6px 0; overflow: hidden; color: #0f172a; font-size: 14px; font-weight: 700; line-height: 1.35; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.card-location { display: flex; align-items: center; gap: 3px; overflow: hidden; color: #64748b; font-size: 11px; white-space: nowrap; }
.card-facts { display: flex; flex-wrap: wrap; gap: 8px 11px; min-height: 30px; margin-top: 11px; color: #475569; font-size: 10px; }
.card-facts span { display: inline-flex; align-items: center; gap: 3px; }
.card-facts .q-icon { color: #94a3b8; font-size: 14px; }
.card-footer { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 12px; padding-top: 10px; border-top: 1px solid #f1f5f9; }
.agent { display: flex; min-width: 0; align-items: center; gap: 7px; color: #64748b; font-size: 10px; }
.agent img { width: 25px; height: 25px; flex: 0 0 25px; border: 1px solid #e8ecf4; border-radius: 50%; object-fit: cover; }
.agent span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.empty-state { display: flex; min-height: 300px; flex-direction: column; align-items: center; justify-content: center; padding: 32px 16px; text-align: center; }
.empty-state h2 { margin: 14px 0 4px; color: #0d1b2a; font-size: 18px; }
.empty-state p { margin: 0 0 14px; color: #64748b; font-size: 13px; }
.pagination-row { display: flex; justify-content: center; padding-top: 30px; }
@media (max-width: 1050px) { .listing-grid { grid-template-columns: repeat(3,minmax(0,1fr)); } }
@media (max-width: 720px) { .listing-grid { grid-template-columns: repeat(2,minmax(0,1fr)); gap: 12px; } .content-wrapper { padding: 0 12px 36px; } .card-body { padding: 11px !important; } .card-facts { gap: 6px; } }
@media (max-width: 440px) { .listing-grid { grid-template-columns: 1fr; } .results-heading { align-items: flex-start; flex-direction: column; } }
</style>
